/**
 * IvyConfirmationModal — Phase 3
 *
 * Surfaces Tier 2 intent actions for explicit user confirmation before
 * execution. No action is taken if the user dismisses or closes the modal.
 *
 * Props:
 *   message         — human-readable confirmation message from intent
 *   actionLabel     — confirm button label (default: "Confirm")
 *   onConfirm       — called when user confirms; modal closes after call
 *   onDismiss       — called when user cancels or clicks outside
 */
import { useEffect, useRef } from 'react'
import { AlertTriangle } from 'lucide-react'

interface IvyConfirmationModalProps {
  message: string
  actionLabel?: string
  onConfirm: () => void
  onDismiss: () => void
}

export default function IvyConfirmationModal({
  message,
  actionLabel = 'Confirm',
  onConfirm,
  onDismiss,
}: IvyConfirmationModalProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)

  // Focus confirm button on open for keyboard accessibility
  useEffect(() => {
    confirmRef.current?.focus()
  }, [])

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDismiss])

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm action"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onDismiss}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative z-10 w-[360px] max-w-[calc(100vw-2rem)] bg-[#0E0E1A] border border-[rgba(167,139,250,0.20)] rounded-2xl p-6 shadow-2xl flex flex-col gap-5">
        {/* Icon + message */}
        <div className="flex gap-3 items-start">
          <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
            <AlertTriangle size={18} className="text-gold" />
          </div>
          <p className="text-sm text-gray-200 leading-relaxed pt-1">{message}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onDismiss}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors rounded-lg hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium bg-red-alert/80 hover:bg-red-alert text-white rounded-lg transition-colors"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
